import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CommonConstants } from '../constants/common.constants';
import { Observable } from 'rxjs';
import { AppConfigService } from 'src/app/core/app-config/app-config.service';
import { SessionStorageService } from './session-storage.service';
import { TranslationService } from './translation.service';
import { CsvHeader, GroupCsvHeader } from 'src/app/file-download/interface/csvHeader';
import { CsvColumns, GroupCsvColumns } from '../enum/csvColumns';
import { CsvRowType, GroupCsvRowType } from '../enum/csvRowType';
import { CsvRowKeys, groupCsvRowKeys } from '../constants/csv.constants';
import { DownloadTypeService } from 'src/app/service/download-type.service';
import { JobType } from '../models/progress.model';
@Injectable({
  providedIn: 'root'
})
export class ExportCsvService {
  private readonly baseRouteDownloadCustomLimitSets: string;
  private readonly baseRouteReDownloadCustomLimitSets: string;
  private readonly baseRouteDownloadAllGroups: string;

  constructor(
    private readonly httpClient: HttpClient,
    @Inject('BASE_URL') private readonly baseUrl: string,
    private readonly appConfigService: AppConfigService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly translationService: TranslationService,
    private readonly downloadTypeService: DownloadTypeService
  ) {
    this.baseRouteDownloadCustomLimitSets = `${baseUrl}api/inventory/recommendation-publications/custom/jobs/download`;
    this.baseRouteReDownloadCustomLimitSets = `${baseUrl}api/inventory/recommendation-publications/custom/download`;
    this.baseRouteDownloadAllGroups = `${baseUrl}bff/groups/download`;
  }

  public downloadCustomLimitSetsAll(): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.httpClient.put(this.baseRouteDownloadCustomLimitSets, { headers });
  }

  public downloadAllGroups(): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.httpClient.put(this.baseRouteDownloadAllGroups, { headers });
  }

  public getDownloadAllGroups(jobRequestCode: string): Observable<any> {
    const url = `${this.baseRouteDownloadAllGroups}?jobRequestCode=${jobRequestCode}`;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'Accept-Encoding': 'gzip' });
    return this.httpClient.get(url, { headers });
  }

  public getCustomRpCsvContent(jobRequestCode: string): Observable<any> {
    const url = `${this.baseRouteReDownloadCustomLimitSets}/${jobRequestCode}`;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'Accept-Encoding': 'gzip' });
    return this.httpClient.get(url, { headers });
  }

  public async downloadRawCSV(csvString: string): Promise<void> {
    const downloadType = this.downloadTypeService.getDownloadType();
    if (downloadType === 0) {
      return;
    }
    const currentLang =
      this.sessionStorageService.getItem<string>(CommonConstants.SESSION_LANGUAGE_CODE) ??
      this.appConfigService.configuration.defaultLanguage;

    let translatedHeaders: string[] = [];

    if (downloadType === JobType.DownloadCustomLimitSets) {
      translatedHeaders = await this.translationService.getTranslatedHeaders(currentLang);
    } else if (downloadType === JobType.DownloadGroups) {
      translatedHeaders = await this.translationService.getGroupTranslatedHeaders(currentLang);
    }

    const rows = csvString.trim().split('\n');
    const headerMapping = translatedHeaders.map((header) => header.trim());
    rows[0] = translatedHeaders.join(',');
    let csv = '';
    if (downloadType === JobType.DownloadCustomLimitSets) {
      const csvData: CsvHeader[] = rows.slice(1).map((row) => {
        const values = this.parseCsvLine(row);
        const rowObject: Partial<CsvHeader> = {};
        CsvRowKeys.forEach((key, index) => {
          rowObject[key] = values[index] ?? '';
        });
        return rowObject as CsvHeader;
      });
      csv = await this.generateCSV(csvData, headerMapping);
    } else if (downloadType === JobType.DownloadGroups) {
      const csvData: GroupCsvHeader[] = rows.slice(1).map((row) => {
        const values = this.parseCsvLine(row);
        const rowObject: Partial<GroupCsvHeader> = {};
        groupCsvRowKeys.forEach((key, index) => {
          rowObject[key] = values[index] ?? '';
        });
        return rowObject as GroupCsvHeader;
      });

      csv = await this.generateGroupCSV(csvData, headerMapping);
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const filename = this.getUniqueFileName(downloadType);

    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private parseCsvLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (const char of line) {
      if (char === "'") {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result.map((v) => v.replace(/^'(.*)'$/, '$1'));
  }

  private getUniqueFileName(type: number): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const formattedDate = `${year}${month}${day}`;
    let filename = '';
    if (type === JobType.DownloadCustomLimitSets) {
      filename = `${CommonConstants.EXPORTLIMITSET}_${formattedDate}.csv`;
    } else if (type === JobType.DownloadGroups) {
      filename = `${CommonConstants.EXPORTGROUPS}_${formattedDate}.csv`;
    }
    return filename;
  }

  private async generateCSV(data: CsvHeader[], translatedHeaders: string[]): Promise<string> {
    const csvHeaders: string[] = [];
    csvHeaders.push(translatedHeaders.map((h) => `"${h.replace(/"/g, '""')}"`).join(','));

    for (const row of data) {
      const values = await Promise.all(
        CsvRowKeys.map(async (key) => {
          let value = row[key] ?? '';
          if (key === CsvColumns.RowType && value === CsvRowType.TestParameter) {
            value = value.replace(/([a-z])([A-Z])/g, '$1 - $2');
          }
          if (
            (key === CsvColumns.RowType && value) ||
            (key === CsvColumns.RuleType && row.RowType === CsvRowType.Definition && value) ||
            (key === CsvColumns.FilterName && row.RowType === CsvRowType.Scope && value) ||
            (key === CsvColumns.Compliancelevel &&
              (row.RowType === CsvRowType.TestParameter || row.RowType === CsvRowType.Parameter) &&
              value)
          ) {
            try {
              value = await this.translationService.getUserLanguageTranslation(value);
            } catch (error) {
              console.error(`Translation failed for ${key}: ${value}`, error);
            }
          }

          return `"${value}"`;
        })
      );

      csvHeaders.push(values.join(','));
    }

    return '\uFEFF' + csvHeaders.join('\n');
  }

  private async generateGroupCSV(data: GroupCsvHeader[], translatedHeaders: string[]): Promise<string> {
    const csvHeaders: string[] = [];
    csvHeaders.push(translatedHeaders.map((h) => `"${h.replace(/"/g, '""')}"`).join(','));

    const rpCodeTypes = [GroupCsvRowType.ParameterDetect, GroupCsvRowType.Arfd, GroupCsvRowType.Mrl, GroupCsvRowType.Count];

    for (const row of data) {
      const values = await Promise.all(
        groupCsvRowKeys.map(async (key) => {
          let value = row[key] ?? '';
          if (
            (key === GroupCsvColumns.RowType && value) ||
            (key === GroupCsvColumns.RuleType && row.RowType === GroupCsvRowType.Definition && value) ||
            (key === GroupCsvColumns.RowType &&
              row.RowType === (GroupCsvRowType.ParameterDetect || GroupCsvRowType.Arfd || GroupCsvRowType.Mrl || GroupCsvRowType.Count) &&
              value)
          ) {
            try {
              value = await this.translationService.getUserLanguageTranslation(value);
            } catch (error) {
              console.error(`Translation failed for ${key}: ${value}`, error);
            }
          }
          if (rpCodeTypes.includes(value as GroupCsvRowType)) {
            value = `RPCode${value}`;
          }

          return `"${value}"`;
        })
      );

      csvHeaders.push(values.join(','));
    }

    return '\uFEFF' + csvHeaders.join('\n');
  }
}
